'use client';

import React from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { FormLabel } from './FormLabel';

export interface FormTextFieldProps extends Omit<TextFieldProps, 'label'> {
  /** The label text to display */
  label?: string;
  /** Whether this field is required (if false/undefined, field is optional) */
  required?: boolean;
  /** Whether the field is in edit mode (required indicator only shows in edit mode) */
  isEditMode?: boolean;
}

/**
 * A TextField wrapper that automatically handles FormLabel integration
 * for consistent required/optional field indicators.
 *
 * @example
 * // Basic usage
 * <FormTextField label="Field Name" />
 *
 * @example
 * // Required field
 * <FormTextField label="Field Name" required />
 *
 * @example
 * // Required field in readonly mode (no asterisk shown)
 * <FormTextField label="Field Name" required isEditMode={false} />
 */
export const FormTextField: React.FC<FormTextFieldProps> = ({
  label,
  required,
  isEditMode = true,
  ...textFieldProps
}) => {
  const formLabel = label ? (
    <FormLabel required={required} optional={!required} isEditMode={isEditMode}>
      {label}
    </FormLabel>
  ) : undefined;

  return <TextField {...textFieldProps} label={formLabel} />;
};

export default FormTextField;
